import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { EventBadges } from "@components/film/EventBadges"
import { Chip } from "@components/ui/Chip"
import { getFilmMetaWithSessions } from "@features/program/get-film-meta"
import { BrowseFilmTileFragment, FilmSessionFragment, ResponsiveImageFragment } from "@generated-types"
import { formatBasicDate, formatNiceDate, formatUpcomingTime } from "@utils/date-format"
import { tv } from "@utils/tw"
import { Link } from "eddev/routing"
import { FunctionComponent, isValidElement, ReactElement, ReactNode, useId } from "react"
import { AnySFFRagDocumentResult, SFFRagDocumentResultTypes } from "../../../server/search/rag"
import { SearchResultItem } from "../utils/SearchResultItem"
import { DocumentTextIcon } from "@heroicons/react/24/outline"
import { SearchDocumentResult } from "../search-system"

export type StandardSearchResultSet<TProps = {}> = {
  [K in keyof SFFRagDocumentResultTypes]?: FunctionComponent<
    SFFRagDocumentResultTypes[K]["data"] & { result: SFFRagDocumentResultTypes[K] } & TProps
  >
}

export function FilmSearchResult(
  props: BrowseFilmTileFragment & {
    session?: FilmSessionFragment
  },
) {
  const filmMeta = getFilmMetaWithSessions(props)

  return (
    <GenericSearchResult
      title={props.title!}
      description={props.descriptions?.short!}
      uri={props.uri!}
      image={props.media?.thumb!}
      typeLabel={props.session ? "" : props.eventInfo?.eventType!}
      trackingType="Film"
      tags={
        <>
          {!props.session &&
            filmMeta.map((label) => (
              <Chip shape="rounded" key={label} variant="high">
                {label}
              </Chip>
            ))}
          {props.session && (
            <>
              <Chip shape="rounded" variant="high">
                {formatUpcomingTime(props.session.info?.dateTime!)}
              </Chip>
              <Chip shape="rounded" variant="high">
                {props.session.info?.venue?.titleSimple}
              </Chip>
            </>
          )}
          <EventBadges shape="rounded" film={props} session={props.session} />
        </>
      }
    />
  )
}

const SEARCH_RESULT_TYPES: StandardSearchResultSet<{}> = {
  film(props) {
    return <FilmSearchResult {...props} />
  },
  venue(props) {
    return (
      <GenericSearchResult
        title={props.title!}
        description={props.descriptions?.description!}
        uri={props.uri!}
        image={props.venueHeader?.image!}
        typeLabel={props.info?.venueCategory!}
        tags={
          <>
            {props.district?.name && (
              <Chip shape="rounded" variant="high">
                {props.district?.name}
              </Chip>
            )}
          </>
        }
      />
    )
  },
  blog(props) {
    return (
      <GenericSearchResult
        title={props.title!}
        description={props.excerpt!}
        uri={props.uri!}
        image={props.info?.heroImage!}
        typeLabel="Article"
        tags={
          <>
            <Chip shape="rounded" variant="high">
              {props.dateGmt ? formatBasicDate(props.dateGmt!) : "Unknown date"}
            </Chip>
            {!!props.info?.category?.name && (
              <Chip shape="rounded" variant="high">
                {props.info?.category?.name}
              </Chip>
            )}
          </>
        }
      />
    )
  },
  strand(props) {
    return (
      <GenericSearchResult
        title={props.title!}
        description={props.excerpt!}
        uri={props.uri!}
        image={props.images?.[0]!}
        typeLabel="Program Strand"
        tags={
          <Chip variant="high" shape="rounded">
            {props.films?.length} films
          </Chip>
        }
      />
    )
  },
  page(props) {
    return (
      <GenericSearchResult
        title={props.title!}
        description={props.excerpt!}
        uri={props.uri!}
        image={props.pageHeader?.headerImage! ?? props.slimSEOMeta?.openGraphImage}
        typeLabel="Page"
        fallbackIcon={<DocumentTextIcon />}
      />
    )
  },
  guest(props) {
    return (
      <GenericSearchResult
        title={props.title!}
        description={props.guestInfo?.bio!}
        uri={"/program/guests/"}
        image={props.guestInfo?.headshot!}
        typeLabel="Guest"
        tags={
          !!props.guestInfo?.role && (
            <Chip shape="rounded" variant="high">
              {props.guestInfo?.role}
            </Chip>
          )
        }
      />
    )
  },
}

export function getSimpleSearchResult(props: { item: SearchDocumentResult }) {
  const { item } = props

  const Component = SEARCH_RESULT_TYPES[item.type]

  if (typeof Component === "function") {
    return <Component {...(props.item.data as any)} result={props.item} />
  }

  return null
}

export function StandardSearchResult(props: { item: SearchDocumentResult }) {
  const { item } = props

  const Component = SEARCH_RESULT_TYPES[item.type]

  if (typeof Component === "function") {
    return <Component {...(props.item.data as any)} result={props.item} />
  }

  return null
}

export function GenericSearchResult(props: {
  image?: ResponsiveImageFragment | { srcSet?: string | null } | ReactElement
  imageOverlay?: ReactNode
  title: ReactNode
  trackingType?: string
  description?: string
  tags?: ReactNode
  typeLabel?: string
  uri: string
  fallbackIcon?: ReactNode
}) {
  const itemStyles = resultStyles({})

  return (
    <SearchResultItem>
      <Link
        href={props.uri}
        className={itemStyles.base()}
        data-mixpanel-result-type={props.trackingType || props.typeLabel}
        data-mixpanel-result-title={props.title}
        data-main-link
      >
        {props.image ? (
          <div className={itemStyles.image()}>
            {isValidElement(props.image) ? props.image : <ResponsiveImage {...props.image} aspect={6 / 4} />}
            {props.imageOverlay}
          </div>
        ) : props.fallbackIcon ? (
          <div className={itemStyles.icon()}>{props.fallbackIcon}</div>
        ) : null}
        <div className={itemStyles.info()}>
          <div className={itemStyles.title()}>{props.title}</div>
          <div className={itemStyles.description()}>
            <div className="line-clamp-2" dangerouslySetInnerHTML={{ __html: props.description! }} />
          </div>
          <div className={itemStyles.tags()}>
            {props.typeLabel && (
              <Chip shape="rounded" variant="high" interactive={false}>
                {props.typeLabel}
              </Chip>
            )}
            {props.tags}
          </div>
        </div>
      </Link>
    </SearchResultItem>
  )
}

const resultStyles = tv({
  base: [
    "@container group",
    "flex flex-row gap-3 align-top [.small-results_&]:!transparent p-[8px] type-ui-m -mx-[8px] rounded-md",
    "[[data-search-item][data-search-selected]_&]:bg-bg-low hover:bg-bg-low transition-colors",
  ],
  slots: {
    info: "flex flex-col gap-[0.5em]",
    image: "w-[26cqw] max-sm:w-[30vw] flex-none aspect-video [&_img]:rounded-sm relative",
    icon: "flex-none flex el-svg:size-[20px] pl-[4px] pt-[4px] text-fg-secondary",
    title: "type-ui-m line-clamp-1",
    description: "type-body-s text-fg-secondary flex-grow",
    tags: "flex flex-row items-start flex-wrap gap-[4px] pointer-events-none overflow-hidden type-label-s h-[1.9em]",
  },
})
